import * as yup from 'yup';

// import {
// LegalEntityContactAddressType,
// LegalEntityDto,
// } from '@/entities/contact/types/legalEntity';
import { CountriesList } from '@/shared/constants/countries-list';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import {
  ActivityList,
  AssetCategory,
  HighMediumLowList,
  OwnershipList,
} from '@/shared/lib/sj-orm/models/asset.dto';
import {
  ContactDto,
  ContactType,
  LegalEntityContactAddressType,
  LegalEntityDto,
} from '@/shared/lib/sj-orm/models/contact.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { PersonalIdentifierType } from '../../sj-orm/models/document/personal-identifier.document.dto';
import { PrivateDocumentCategory } from '../../sj-orm/models/document/private.document.dto';
import { CollectionName } from '../../sj-orm/types';

const LegalEntityContactAddressTypesLst = [
  {
    label: 'forms_contacts:legalEntity.enums.addressType.legal',
    key: LegalEntityContactAddressType.LEGAL,
  },
  {
    label: 'forms_contacts:legalEntity.enums.addressType.other',
    key: LegalEntityContactAddressType.OTHER,
  },
];

type LegalEntityContactDataPartType = Omit<LegalEntityDto, 'id' | 'type'>;

type LegalEntityContactHelpPartType = {
  nickname: QuestionType.TEXT;
  selectedCountry: QuestionType.SELECT_FROM_LIST;
  phone?: QuestionType.PHONE_NUMBER;
  email?: QuestionType.EMAIL;
  //address
  addressType?: QuestionType.SELECT_FROM_LIST;
  address?: QuestionType.TEXT;
  fullLegalName?: QuestionType.TEXT;
  activityType?: QuestionType.SELECT_FROM_LIST;
  trustLevel?: QuestionType.SELECT_FROM_LIST;
  contactPerson?: QuestionType.PICK_ARRAY_OF_DTO; // individual contact
  // asset info
  associatedWithAnAsset?: QuestionType.CHECKBOX;
  assetId?: QuestionType.PICK_ARRAY_OF_DTO;
  assetCommentary?: QuestionType.TEXT;
  // company info
  legalForm?: QuestionType.TEXT;
  businessType?: QuestionType.TEXT;
  dateOfIncorporation?: QuestionType.DATE_TIME;
  taxNumber?: QuestionType.TEXT;
  taxNumberIssuer?: QuestionType.TEXT;
  lei?: QuestionType.TEXT;
  // company owner info
  ownershipType?: QuestionType.SELECT_FROM_LIST;
  owner?: QuestionType.PICK_ARRAY_OF_DTO;
  ownerDocument: QuestionType.PICK_ARRAY_OF_DTO; // personal id
  ownerOtherDocument: QuestionType.PICK_ARRAY_OF_DTO; // private
  documents: QuestionType.PICK_ARRAY_OF_DTO; // private
  commentary: QuestionType.TEXT_MULTILINE;
};

type LegalEntityContactFormType = TDtoWrapper<
  LegalEntityContactDataPartType,
  LegalEntityContactHelpPartType
>;

const yupRequiredField = yup.string().required('common:thisIsRequiredField');

const LegalEntityContactForm: LegalEntityContactFormType = {
  nickname: {
    type: QuestionType.TEXT,
    label: 'forms_contacts:common.inputs.nickname.label',
    required: true,
    value: '',
    yupScheme: yupRequiredField,
  },
  selectedCountry: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_contacts:common.inputs.selectedCountry.label',
    required: true,
    value: '',
    items: CountriesList,
    withSearch: true,
    yupScheme: yupRequiredField,
  },
  fullLegalName: {
    type: QuestionType.TEXT,
    label: 'forms_contacts:legalEntity.inputs.fullLegalName.label',
  },
  phone: {
    type: QuestionType.PHONE_NUMBER,
    label: 'forms_contacts:common.inputs.phoneNumber.label',
  },
  email: {
    type: QuestionType.EMAIL,
    label: 'forms_contacts:common.inputs.email.label',
  },
  //address
  addressType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_contacts:legalEntity.inputs.addressType.label',
    items: LegalEntityContactAddressTypesLst,
  },
  address: {
    type: QuestionType.TEXT,
    label: 'forms_contacts:common.inputs.address.label',
  },
  activityType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_contacts:legalEntity.inputs.activityType.label',
    items: ActivityList,
  },
  trustLevel: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_contacts:legalEntity.inputs.trustLevel.label',
    items: HighMediumLowList,
  },
  contactPerson: {
    type: QuestionType.PICK_ARRAY_OF_DTO, // individual contact
    label: 'forms_contacts:legalEntity.inputs.contactPerson.label',
    items: CollectionName.CONTACTS,
    dtoType: ContactType.INDIVIDUAL,
    filter: (item) => (item as ContactDto).type === ContactType.INDIVIDUAL,
  },
  // asset info
  associatedWithAnAsset: {
    type: QuestionType.CHECKBOX,
    label: 'forms_contacts:legalEntity.inputs.associatedWithAnAsset.label',
  },
  assetId: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_contacts:legalEntity.inputs.assetId.label',
    items: CollectionName.ASSETS,
    showIf: {
      associatedWithAnAsset: {
        value: true,
      },
    },
    dtoType: AssetCategory.BANK_CELLS,
  },
  assetCommentary: {
    type: QuestionType.TEXT,
    label: 'forms_contacts:legalEntity.inputs.assetCommentary.label',
    showIf: {
      associatedWithAnAsset: {
        value: true,
      },
    },
  },
  // company info
  legalForm: {
    type: QuestionType.TEXT,
    label: 'forms_contacts:legalEntity.inputs.legalForm.label',
  },
  businessType: {
    type: QuestionType.TEXT,
    label: 'forms_contacts:legalEntity.inputs.businessType.label',
  },
  dateOfIncorporation: {
    type: QuestionType.DATE_TIME,
    label: 'forms_contacts:legalEntity.inputs.dateOfIncorporation.label',
  },
  taxNumber: {
    type: QuestionType.TEXT,
    label: 'forms_contacts:legalEntity.inputs.taxNumber.label',
  },
  taxNumberIssuer: {
    type: QuestionType.TEXT,
    label: 'forms_contacts:legalEntity.inputs.taxNumberIssuer.label',
  },
  lei: {
    type: QuestionType.TEXT,
    label: 'forms_contacts:legalEntity.inputs.lei.label',
  },
  // company owner info
  ownershipType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_contacts:legalEntity.inputs.ownershipType.label',
    items: OwnershipList,
  },
  owner: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_contacts:legalEntity.inputs.owner.label',
    items: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
    dtoType: PersonalIdentifierType.BIRTH_CERTIFICATE,
  },
  ownerDocument: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_contacts:legalEntity.inputs.ownerDocument.label',
    items: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
    dtoType: PersonalIdentifierType.BIRTH_CERTIFICATE,
    mode: 'multiple',
  },
  ownerOtherDocument: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_contacts:legalEntity.inputs.ownerOtherDocument.label',
    items: CollectionName.PRIVATE_DOCUMENTS,
    dtoType: PrivateDocumentCategory.OTHER,
    mode: 'multiple',
  },
  documents: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_contacts:legalEntity.inputs.documents.label',
    items: CollectionName.PRIVATE_DOCUMENTS,
    mode: 'multiple',
    dtoType: PrivateDocumentCategory.OTHER,
  },
  commentary: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_contacts:legalEntity.inputs.comment.label',
  },
};

export { LegalEntityContactForm };
